// useAdminSync — four live subscriptions for the worker view.
//
//   audios : all 15, orderBy order asc
//   users  : all 25 (14 learners + 11 workers), orderBy no asc
//   markers: parent metadata + pins subcollection (PR-B1: dual-shape)
//   regions: all 4, orderBy order asc
//
// PR-B1 (Day 13): markers now has a `pins: PinWithId[]` array attached.
// If a parent /markers/{uid_rid} doc has no pins subcollection yet (Day 12
// legacy data), we synthesize a single PinDoc from the parent's leaf
// fields (lat/lng/clue) so existing markers keep showing while migration
// runs. After migration, all real data lives in /pins/.
//
// Workers see everything; Firestore Rules already enforce isWorker for writes.

import {
  type AudioDoc,
  type FragmentDoc,
  type ListenDoc,
  type MarkerDoc,
  type MessageDoc,
  type PinDoc,
  type RegionDoc,
  type UserDoc,
  type WithId,
  allListensQuery,
  allPinsQuery,
  audiosCol,
  fragmentsCol,
  markersCol,
  messagesCol,
  regionsCol,
  usersCol,
  useFirestoreCollection,
} from "@goodbye/firebase";
import { Timestamp, orderBy, query } from "firebase/firestore";
import { useMemo } from "react";

export type MarkerWithPins = WithId<MarkerDoc> & {
  pins: WithId<PinDoc>[];
};

export interface AdminSyncState {
  audios: WithId<AudioDoc>[];
  users: WithId<UserDoc>[];
  markers: MarkerWithPins[];
  regions: WithId<RegionDoc>[];
  messages: WithId<MessageDoc>[];
  fragments: WithId<FragmentDoc>[];
  /** 2026-05-30: all audio-listen records (every audio × learner who opened
   *  it). Each carries audioId + userId. AudioRelease groups by audioId. */
  listens: WithId<ListenDoc>[];
  loading: boolean;
  error: Error | null;
}

/**
 * Synthesize a legacy pin from a Day 12-era marker doc that has lat/lng/clue
 * at the parent but no pins subcollection yet. Lets the UI keep working
 * during the migration window.
 */
function legacyPinFromMarker(m: WithId<MarkerDoc>): WithId<PinDoc> | null {
  if (typeof m.lat !== "number" || typeof m.lng !== "number") return null;
  return {
    id: "legacy",
    userId: m.userId,
    regionId: m.regionId,
    lat: m.lat,
    lng: m.lng,
    clue: m.clue ?? "（無提示）",
    placedBy: m.placedBy,
    placedAt: m.placedAt,
    foundAt: m.foundAt,
  };
}

export function useAdminSync(): AdminSyncState {
  const audiosQ = useMemo(() => query(audiosCol(), orderBy("order", "asc")), []);
  const usersQ = useMemo(() => query(usersCol(), orderBy("no", "asc")), []);
  const markersQ = useMemo(() => markersCol(), []);
  const regionsQ = useMemo(() => query(regionsCol(), orderBy("order", "asc")), []);
  const pinsQ = useMemo(() => allPinsQuery(), []);
  const messagesQ = useMemo(
    () => query(messagesCol(), orderBy("createdAt", "desc")),
    [],
  );
  const fragmentsQ = useMemo(
    () => query(fragmentsCol(), orderBy("createdAt", "desc")),
    [],
  );
  const listensQ = useMemo(() => allListensQuery(), []);

  const audios = useFirestoreCollection(audiosQ);
  const users = useFirestoreCollection(usersQ);
  const markers = useFirestoreCollection(markersQ);
  const pins = useFirestoreCollection(pinsQ);
  const regions = useFirestoreCollection(regionsQ);
  const messages = useFirestoreCollection(messagesQ);
  const fragments = useFirestoreCollection(fragmentsQ);
  const listens = useFirestoreCollection(listensQ);

  const markersWithPins: MarkerWithPins[] = useMemo(() => {
    const pinsByMarkerId = new Map<string, WithId<PinDoc>[]>();
    for (const p of pins.data) {
      const key = `${p.userId}_${p.regionId}`;
      const list = pinsByMarkerId.get(key) ?? [];
      list.push(p);
      pinsByMarkerId.set(key, list);
    }
    return markers.data.map((m) => {
      const realPins = pinsByMarkerId.get(m.id) ?? [];
      const finalPins =
        realPins.length > 0
          ? realPins
          : [legacyPinFromMarker(m)].filter(
              (x): x is WithId<PinDoc> => x !== null,
            );
      return { ...m, pins: finalPins };
    });
  }, [markers.data, pins.data]);

  return {
    audios: audios.data,
    users: users.data,
    markers: markersWithPins,
    regions: regions.data,
    messages: messages.data,
    fragments: fragments.data,
    listens: listens.data,
    loading:
      audios.loading ||
      users.loading ||
      markers.loading ||
      pins.loading ||
      regions.loading ||
      messages.loading ||
      fragments.loading ||
      listens.loading,
    error:
      audios.error ??
      users.error ??
      markers.error ??
      pins.error ??
      regions.error ??
      messages.error ??
      fragments.error ??
      listens.error,
  };
}

// Re-export Timestamp just to keep the import used; legacyPinFromMarker
// might inherit a Timestamp via spread.
void Timestamp;
